import Vue from 'vue'
import {
  FilteringAttributeType,
  MappedPriceFilter,
  PriceFilterItem,
  Products,
  TransformedFiltersMapping,
} from '@one/types'
import { BasePagination } from '~/utils/BasePagination'
import SortingOption = Products.SortingOption
import SearchResultPager = Products.Responses.SearchResultPager
import TransformedFiltersResponse = Products.Responses.TransformedFiltersResponse

export type PriceType = 'NET' | 'GROSS'

export interface PredefinedFilter {
  value: string,
  code: string
  codes?: Array<string>
}
export interface FilterChangeEvent {
  nameCode: string,
  type: FilteringAttributeType
}
export interface MultiCheckboxFilterChangeEvent extends FilterChangeEvent {
  type: FilteringAttributeType.MULTICHECKBOX
  isSelected: boolean
  filterValue: string
}
export interface RangeFilterChangeEvent extends FilterChangeEvent {
  type: FilteringAttributeType.RANGE
  filterValue: string
}
export interface InputFilterChangeEvent extends FilterChangeEvent {
  type: FilteringAttributeType.INPUT
  value: string
}
export interface PricingFilterChangeEvent extends FilterChangeEvent {
  type: FilteringAttributeType.PRICE
  value: [number, number]
}

export interface ProductsListingOptions {
  priceType: PriceType
}

export type CombinedFilterChangeEvent =
  MultiCheckboxFilterChangeEvent
  | RangeFilterChangeEvent
  | InputFilterChangeEvent

export class ProductsListing extends BasePagination {
  priceType: PriceType = 'NET'
  priceFilter: MappedPriceFilter | null = null;
  priceFilterValue: [number, number] = [0, 0]
  priceFilterQuery: string | null = null
  filtersQuery: Array<string> = [];
  rangeFiltersQuery: Array<string> = [];

  attributes: TransformedFiltersMapping = {
    byId: {},
    allIds: [],
  };

  currentCategoryId: string | null = null;
  categoriesCount: Record<string, number> = {};
  selectedFilters: Record<string, Array<string>> = {};
  sortingOptions: Array<SortingOption> = [];
  filtersUpdateFunctions = {
    [FilteringAttributeType.MULTICHECKBOX]:
      (event: CombinedFilterChangeEvent) => this.multiCheckboxFilterUpdateHandler(this.filtersQuery, event as MultiCheckboxFilterChangeEvent),
    [FilteringAttributeType.RANGE]: (event: CombinedFilterChangeEvent) => this.rangeFilterUpdateHandler(this.rangeFiltersQuery, event as RangeFilterChangeEvent),
    [FilteringAttributeType.INPUT]: () => ([]),
  }

  public static initializeListing() {
    return Vue.observable(new ProductsListing())
  }

  private get isPriceFilterAvailable(): boolean {
    return this.priceFilter !== null
  }

  public updateListing(products: SearchResultPager, filters: TransformedFiltersResponse) {
    this.updateProducts(products)
    this.updateFilters(filters)
  }

  public selectCategory(id: string | null) {
    this.currentCategoryId = id
  }

  public get queryForProducts(): Products.Requests.ProductsPagination {
    return {
      q: this.q,
      category: this.currentCategoryId ?? undefined,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortCriteria: this.sortCriteria,
      filtersQuery: this.filtersQuery,
      rangeFiltersQuery: this.rangeFiltersQuery,
      price: this.priceFilterQuery ?? undefined,
    }
  }

  public get queryForFilters(): Products.Requests.FiltersPagination {
    return {
      q: this.q,
      category: this.currentCategoryId ?? undefined,
      filtersQuery: this.filtersQuery,
      rangeFiltersQuery: this.rangeFiltersQuery,
      price: this.priceFilterQuery ?? undefined,
    }
  }

  private setPageAsLoaded(page: number, items: Array<string>) {
    this.addLoadedPage(page, {
      loaded: true,
      items,
    })
  }

  public updateProducts(products: SearchResultPager) {
    this.totalPages = products.totalPages
    this.totalItems = products.totalItems
    this.sortingOptions = products.sortingOptions
    this.setPageAsLoaded(products.pageNumber, products.items.map(p => p.id))
  }

  public updateFilters(response: TransformedFiltersResponse) {
    this.attributes = response.transformedFilters
    this.selectedFilters = response.selectedFilters
    this.categoriesCount = response.filters.categoriesCount
    const priceFilterFromResponse: Array<PriceFilterItem> = response.filters.priceFilters
    this.priceFilter = priceFilterFromResponse.length
? {
      type: FilteringAttributeType.PRICE,
      NET: priceFilterFromResponse.find(x => x.name === 'NET')!,
      GROSS: priceFilterFromResponse.find(x => x.name === 'GROSS')!,
    }
: null
    if (this.isPriceFilterUsed) {
      this.setPrideFilterToDefaultIfNotValid()
    } else {
      this.clearPriceFilter()
    }
  }

  public get productsForCurrentPage(): Array<string> {
    return this.loadedPages[this.pageNumber]?.items ?? []
  }

  public nextPage(): void {
    const nextPage: number = this.pageNumber + 1
    if (this.canGetPage(nextPage)) {
      super.nextPage()
    }
  }

  public previousPage(): void {
    const prevPage: number = this.pageNumber - 1
    if (this.canGetPage(prevPage)) {
      super.previousPage()
    }
  }

  public setPageNumber(page: number): boolean {
    if (this.canGetPage(page)) {
      super.setPageNumber(page)
      return true
    }
    return false
  }

  public setPredefinedFilters(filtersQuery: Array<PredefinedFilter>) {
    this.filtersQuery = filtersQuery.flatMap((filter) => {
      if (filter.codes?.length) {
        return filter.codes.map((code: string) => `${filter.value}:${code}`)
      }
      return `${filter.value}:${filter.code}`
    })
  }

  public setFilters(filtersQuery: Array<string>) {
    this.filtersQuery = filtersQuery
  }

  public setRangeFilters(rangeFiltersQuery: Array<string>) {
    this.rangeFiltersQuery = rangeFiltersQuery
  }

  private multiCheckboxFilterUpdateHandler = (filters: Array<string>, event: MultiCheckboxFilterChangeEvent): Array<string> => {
    const newFilter: string = `${event.nameCode}:${event.filterValue}`
    const indexOfChangedFilter: number = filters.indexOf(newFilter)
    if (indexOfChangedFilter > -1) {
      filters.splice(indexOfChangedFilter, 1)
    } else {
      filters.push(newFilter)
    }
    return filters
  }

  private rangeFilterUpdateHandler = (filters: Array<string>, event: RangeFilterChangeEvent): Array<string> => {
    const prefix: string = `${event.nameCode}:`
    const newFilter: string = `${prefix}${event.filterValue}`
    const indexOfChangedFilter: number = filters.findIndex((filter) => filter.startsWith(prefix))
    if (indexOfChangedFilter > -1) {
      filters.splice(indexOfChangedFilter, 1, newFilter)
    } else {
      filters.push(newFilter)
    }
    return filters
  }

  public updateFiltersQuery(event: CombinedFilterChangeEvent) {
    this.filtersQuery = this.filtersUpdateFunctions[event.type](event)
  }

  public setSearchQuery(query: string) {
    this.q = query
  }

  private changePriceFilterValue(value: [number, number]) {
    if (this.isPriceFilterAvailable) {
      this.priceFilterValue = value
    }
  }

  private updatePriceFilterQuery() {
    if (this.isPriceFilterAvailable) {
      this.priceFilterQuery = `${this.priceType}:${this.priceFilterValue[0]}-${this.priceFilterValue[1]}`
    }
  }

  private get isPriceFilterUsed(): boolean {
    return this.priceFilterQuery !== null
  }

  private get defaultPriceFilterValue(): [number, number] {
    if (this.isPriceFilterAvailable) {
      const min: number = this.priceFilter![this.priceType].min
      const max: number = this.priceFilter![this.priceType].max
      return [min, max]
    }
    return [0, 0]
  }

  private isValueInPriceRange(value: [number, number]): boolean {
    return this.priceFilter![this.priceType].min <= value[0] && value[1] <= this.priceFilter![this.priceType].max
  }

  public priceFilterValueChanged = (event: PricingFilterChangeEvent): void => {
    this.changePriceFilterValue(event.value)
    this.updatePriceFilterQuery()
  }

  public setPriceType(priceType: PriceType) {
    this.priceType = priceType
    this.changePriceFilterValue(this.defaultPriceFilterValue)
    this.updatePriceFilterQuery()
  }

  public setPrideFilterToDefaultIfNotValid() {
    if (this.isPriceFilterAvailable) {
      if (!this.isValueInPriceRange(this.priceFilterValue)) {
        this.setPriceFilterToDefault()
      }
    }
  }

  public setPriceFilterToDefault() {
    this.changePriceFilterValue(this.defaultPriceFilterValue)
    this.updatePriceFilterQuery()
  }

  public clearPriceFilter(): void {
    this.changePriceFilterValue(this.defaultPriceFilterValue)
    this.priceFilterQuery = null
  }
}
